// Order placement. Routes through the placeOrder Cloud Function when a backend is
// configured (server-side stock transaction + fee validation), otherwise simulates
// the order in the demo store.
import { httpsCallable } from "firebase/functions";
import { functions } from "./firebase";
import { isBackendConfigured } from "./catalog";
import { demoStore } from "./demoStore";
import type { Address, CartLine, Customer, Order, PaymentMethod } from "./types";

export interface PlaceOrderInput {
  branchId: string;
  fulfillmentType?: "delivery" | "pickup";
  /** Requested time (epoch ms); null/absent = ASAP. */
  scheduledFor?: number | null;
  customerUid?: string | null;
  customer: Customer;
  address: Address;
  /** Present in pin mode; null in manual-area mode. */
  geo: { lat: number; lng: number } | null;
  distanceKm: number;
  /** Present in manual-area mode; the server resolves the fee from the band. */
  deliveryZone: { band: number; area: string } | null;
  /** Client-computed fee — used only to record accurate demo totals; the backend
   *  recomputes the fee server-side and ignores this. */
  deliveryFee?: number;
  /** Promo discount off the subtotal (never the delivery fee). Backend re-validates. */
  discount?: number;
  promoCode?: string;
  lines: CartLine[];
  paymentMethod: PaymentMethod;
}

export interface PlaceOrderResult {
  orderId: string;
  /** Present for online payments — redirect the customer here to pay. */
  paymentUrl?: string;
  order?: Order;
}

export async function placeOrder(input: PlaceOrderInput): Promise<PlaceOrderResult> {
  if (!isBackendConfigured) {
    return placeOrderDemo(input);
  }
  const call = httpsCallable<PlaceOrderInput, PlaceOrderResult>(functions, "placeOrder");
  const res = await call(input);
  return res.data;
}

function placeOrderDemo(input: PlaceOrderInput): PlaceOrderResult {
  const subtotal = round3(input.lines.reduce((s, l) => s + l.price * l.qty, 0));
  // Demo stock check (mirrors the server transaction). Aggregate by itemId so
  // multiple lines of the same daily item can't collectively oversell stock.
  const dailyNeeded = new Map<string, number>();
  for (const line of input.lines.filter((l) => l.type === "daily")) {
    dailyNeeded.set(line.itemId, (dailyNeeded.get(line.itemId) ?? 0) + line.qty);
  }
  for (const [itemId, qty] of dailyNeeded) {
    const item = demoStore.getDaily().find((d) => d.id === itemId);
    if (!item || item.remainingQty < qty) {
      throw new Error(`SOLD_OUT:${itemId}`);
    }
  }
  demoStore.applyOrderToStock(input.lines);

  const orderId = "KB" + String(Math.floor(performance.now())).slice(-6).padStart(6, "0");
  const deliveryFee = round3(input.deliveryFee ?? 0);
  const discount = round3(Math.min(input.discount ?? 0, subtotal));
  const order: Order = {
    id: orderId,
    branchId: input.branchId,
    fulfillmentType: input.fulfillmentType ?? "delivery",
    scheduledFor: input.scheduledFor ?? null,
    customerUid: input.customerUid ?? null,
    customer: input.customer,
    address: input.address,
    geo: input.geo,
    distanceKm: input.distanceKm,
    lines: input.lines,
    subtotal,
    discount,
    promoCode: input.promoCode,
    deliveryFee,
    total: round3(subtotal - discount + deliveryFee),
    paymentMethod: input.paymentMethod,
    paymentStatus: input.paymentMethod === "cod" ? "unpaid" : "pending",
    orderStatus: "new",
    createdAt: Date.now(),
  };
  demoStore.addOrder(order);
  return { orderId, order };
}

function round3(n: number): number {
  return Math.round((n + Number.EPSILON) * 1000) / 1000;
}
